import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FaLock, FaUnlock, FaSpinner } from 'react-icons/fa';
import { wheelApi } from '../services/api.ts';
import { Wheel } from '../components/Wheel.tsx';

// Define the wheel type to match the actual data structure
interface WheelType {
  id: string;
  name: string;
  description: string;
  slotCount: number;
  currentRound: number;
  lastSpinDate?: string;
  slots: any[];
  activeUsers: any[];
}

// Styled components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  
  svg {
    font-size: 3rem;
    color: #4a90e2;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  p {
    margin-top: 1rem;
    font-size: 1.2rem;
    color: #666;
  }
`;

const ErrorContainer = styled.div`
  background-color: #fff3f3;
  border: 1px solid #ffcaca;
  border-radius: 8px;
  padding: 2rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
  
  h3 {
    color: #e53935;
    margin-bottom: 1rem;
  }
  
  p {
    color: #666;
    margin-bottom: 1.5rem;
  }
`;

const AccessCodeForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem auto;
  max-width: 400px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const Button = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    background-color: #3a7bc8;
  }
  
  &:disabled {
    background-color: #a0c3e8;
    cursor: not-allowed;
  }
`;

const WheelInfo = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: #555;
  margin-right: 0.5rem;
`;

const InfoValue = styled.span`
  color: #333;
`;

const PrivateWheelPage: React.FC = () => {
  const params = useParams<{ wheelId: string }>();
  const wheelId = params.wheelId;
  const navigate = useNavigate();
  
  const [wheel, setWheel] = useState<WheelType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  
  useEffect(() => {
    const fetchWheel = async () => {
      if (!wheelId) {
        setError('No wheel ID provided');
        setLoading(false);
        return;
      }
      
      try {
        const data = await wheelApi.getWheelById(wheelId);
        setWheel(data);
        
        // Check if this is a public wheel or if we already have access
        // This logic may need to be adjusted based on your actual API response
        const hasAccess = !data.requiresAccessCode;
        setIsVerified(hasAccess);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching wheel:', error);
        setError('Could not fetch the wheel. It may not exist or you may not have permission to view it.');
        setLoading(false);
      }
    };
    
    fetchWheel();
  }, [wheelId]);
  
  const handleAccessCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!wheelId || !accessCode.trim()) return;
    
    setVerifying(true);
    
    try {
      const data = await wheelApi.getWheelByAccessCode(wheelId, accessCode);
      setWheel(data);
      setIsVerified(true);
      setError(null);
    } catch (error) {
      console.error('Error verifying access code:', error);
      setError('Invalid access code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };
  
  if (loading) {
    return (
      <Container>
        <Helmet>
          <title>Loading Wheel - Spin To Connect</title>
          <meta name="description" content="Loading your private or shared Spin To Connect wheel experience." />
        </Helmet>
        <Header>
          <Title>Private Wheel</Title>
        </Header>
        <LoadingContainer>
          <FaSpinner />
          <p>Loading wheel information...</p>
        </LoadingContainer>
      </Container>
    );
  }
  
  if (error && !wheel) {
    return (
      <Container>
        <Helmet>
          <title>Wheel Not Found - Spin To Connect</title>
          <meta name="description" content="The Spin To Connect wheel you are looking for may not exist or is inaccessible." />
        </Helmet>
        <Header>
          <Title>Private Wheel</Title>
        </Header>
        <ErrorContainer>
          <h3>Error</h3>
          <p>{error}</p>
          <Button onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </ErrorContainer>
      </Container>
    );
  }
  
  if (wheel && !isVerified) {
    return (
      <Container>
        <Helmet>
          <title>{wheel.name ? wheel.name + ' - Access Required' : 'Private Wheel - Access Required'} - Spin To Connect</title>
          <meta name="description" content={wheel.description ? wheel.description + ' | This Spin To Connect wheel requires an access code for entry.' : 'This Spin To Connect wheel requires an access code for entry.'} />
        </Helmet>
        <Header>
          <Title>{wheel.name}</Title>
          <Description>{wheel.description}</Description>
        </Header>
        
        <WheelInfo>
          <InfoItem>
            <InfoLabel>Wheel ID:</InfoLabel>
            <InfoValue>{wheel.id}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Slots:</InfoLabel>
            <InfoValue>{wheel.slotCount}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Current Round:</InfoLabel>
            <InfoValue>{wheel.currentRound}</InfoValue>
          </InfoItem>
        </WheelInfo>
        
        <ErrorContainer>
          <h3><FaLock /> This wheel requires an access code</h3>
          <p>Please enter the access code provided by the wheel owner to view this wheel.</p>
          
          {error && <p style={{ color: '#e53935' }}>{error}</p>}
          
          <AccessCodeForm onSubmit={handleAccessCodeSubmit}>
            <Input
              type="text"
              placeholder="Enter access code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              required
            />
            <Button type="submit" disabled={verifying}>
              {verifying ? <FaSpinner /> : <FaUnlock />}
              {verifying ? 'Verifying...' : 'Unlock Wheel'}
            </Button>
          </AccessCodeForm>
        </ErrorContainer>
      </Container>
    );
  }
  
  return (
    <Container>
      <Helmet>
        <title>{wheel?.name ? wheel.name + ' - Spin To Connect' : 'Wheel Details - Spin To Connect'}</title>
        <meta name="description" content={wheel?.description || 'Explore this interactive Spin To Connect wheel.'} />
      </Helmet>
      <Header>
        <Title>{wheel?.name}</Title>
        <Description>{wheel?.description}</Description>
      </Header>
      
      {wheel && <Wheel wheelData={wheel} />}
    </Container>
  );
};

export default PrivateWheelPage; 